import { useEffect, useState } from "react";
import { toast } from "sonner";
import { EnvelopeSimple, Plus, Trash, Eye, PaperPlaneTilt, Clock } from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";
import { useLang } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const DAY_KEYS = ["dayMon", "dayTue", "dayWed", "dayThu", "dayFri", "daySat", "daySun"];

export default function WeeklyReportPanel() {
  const { t } = useLang();
  const [cfg, setCfg] = useState({ enabled: false, day_of_week: 0, hour: 7, recipients: [] });
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);
  const [previewing, setPreviewing] = useState(false);

  const load = async () => {
    try {
      const [{ data: s }, { data: h }] = await Promise.all([
        api.get("/weekly-report/settings"),
        api.get("/weekly-report/history").catch(() => ({ data: [] })),
      ]);
      setCfg({
        enabled: !!s.enabled,
        day_of_week: Number(s.day_of_week ?? 0),
        hour: Number(s.hour ?? 7),
        recipients: s.recipients || [],
      });
      setHistory(h || []);
    } catch (err) { toast.error(apiError(err)); }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/weekly-report/settings", {
        enabled: cfg.enabled,
        day_of_week: cfg.day_of_week,
        hour: cfg.hour,
        recipients: cfg.recipients.filter(r => r.email && r.email.includes("@")),
      });
      setCfg({
        enabled: !!data.enabled,
        day_of_week: Number(data.day_of_week ?? 0),
        hour: Number(data.hour ?? 7),
        recipients: data.recipients || [],
      });
      toast.success(t("themeSaved"));
    } catch (err) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  };

  const addRecipient = () =>
    setCfg(c => ({ ...c, recipients: [...c.recipients, { email: "", name: "", lang: "es" }] }));

  const updateRecipient = (idx, patch) =>
    setCfg(c => ({ ...c, recipients: c.recipients.map((r, i) => i === idx ? { ...r, ...patch } : r) }));

  const removeRecipient = (idx) =>
    setCfg(c => ({ ...c, recipients: c.recipients.filter((_, i) => i !== idx) }));

  const sendNow = async () => {
    if (!cfg.recipients.length) {
      toast.error(t("sentNoConfig"));
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/weekly-report/send-now");
      if (data.ok) {
        if (data.failed > 0) {
          toast.warning(t("sentPartial", { ok: data.sent, fail: data.failed }));
          // Surface actionable reasons — e.g. sender_no_verificado tells the
          // operator the fix requires verifying a domain on resend.com.
          const reasons = (data.results || []).filter(r => r.status !== "enviado");
          if (reasons.some(r => r.status === "sender_no_verificado")) {
            toast.error(t("weeklySenderUnverified"), { duration: 15000 });
          } else if (reasons.length) {
            toast.error(reasons[0].status, { duration: 10000 });
          }
        } else {
          toast.success(t("sentSuccess", { count: data.sent }));
        }
        load();
      } else {
        toast.error(data.reason || "error");
      }
    } catch (err) { toast.error(apiError(err)); }
    finally { setSending(false); }
  };

  const preview = async () => {
    setPreviewing(true);
    try {
      const { data } = await api.get("/weekly-report/preview", { params: { lang: "es" } });
      const w = window.open("", "_blank", "width=760,height=900");
      if (w) {
        w.document.write(`<title>${data.subject}</title>` + data.html);
        w.document.close();
      }
    } catch (err) { toast.error(apiError(err)); }
    finally { setPreviewing(false); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-8 max-w-4xl" data-testid="weekly-report-panel">
      <div className="flex items-center gap-2 mb-1">
        <EnvelopeSimple size={20} weight="duotone" className="text-blue-900" />
        <Label className="overline-label">{t("weeklyReport")}</Label>
      </div>
      <p className="text-sm text-slate-500 mb-6">{t("weeklyReportHint")}</p>

      <div className="flex items-center justify-between p-4 bg-slate-50 border border-slate-200 rounded-md mb-6">
        <div>
          <p className="text-sm font-semibold text-slate-800">{t("enableWeekly")}</p>
          {cfg.enabled && (
            <p className="text-xs text-slate-500 mt-1 flex items-center gap-1.5">
              <Clock size={13} /> {t(DAY_KEYS[cfg.day_of_week])} · {String(cfg.hour).padStart(2, "0")}:00 UTC
            </p>
          )}
        </div>
        <Switch
          data-testid="weekly-enabled-toggle"
          checked={cfg.enabled}
          onCheckedChange={(v) => setCfg(c => ({ ...c, enabled: !!v }))}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div>
          <Label className="overline-label" htmlFor="weekly-day">{t("weekDay")}</Label>
          <Select value={String(cfg.day_of_week)} onValueChange={(v) => setCfg(c => ({ ...c, day_of_week: Number(v) }))}>
            <SelectTrigger id="weekly-day" data-testid="weekly-day-select" className="mt-1.5">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DAY_KEYS.map((k, i) => (
                <SelectItem key={k} value={String(i)} data-testid={`weekly-day-${i}`}>{t(k)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="overline-label" htmlFor="weekly-hour">{t("sendHour")}</Label>
          <Select value={String(cfg.hour)} onValueChange={(v) => setCfg(c => ({ ...c, hour: Number(v) }))}>
            <SelectTrigger id="weekly-hour" data-testid="weekly-hour-select" className="mt-1.5">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Array.from({ length: 24 }, (_, i) => (
                <SelectItem key={i} value={String(i)}>{String(i).padStart(2, "0")}:00</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <Label className="overline-label">{t("recipients")}</Label>
          <Button
            variant="outline"
            size="sm"
            onClick={addRecipient}
            data-testid="weekly-add-recipient"
            className="gap-1.5"
          >
            <Plus size={14} weight="bold" /> {t("addRecipient")}
          </Button>
        </div>

        {cfg.recipients.length === 0 ? (
          <p className="text-sm text-slate-400 italic py-4 text-center border border-dashed border-slate-200 rounded-md">
            {t("sentNoConfig")}
          </p>
        ) : (
          <div className="space-y-2">
            {cfg.recipients.map((r, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`weekly-recipient-${i}`}>
                <Input
                  className="col-span-5"
                  placeholder={t("recipientEmail")}
                  value={r.email}
                  data-testid={`weekly-recipient-${i}-email`}
                  onChange={(e) => updateRecipient(i, { email: e.target.value })}
                />
                <Input
                  className="col-span-4"
                  placeholder={t("recipientName")}
                  value={r.name || ""}
                  data-testid={`weekly-recipient-${i}-name`}
                  onChange={(e) => updateRecipient(i, { name: e.target.value })}
                />
                <Select value={r.lang || "es"} onValueChange={(v) => updateRecipient(i, { lang: v })}>
                  <SelectTrigger className="col-span-2" data-testid={`weekly-recipient-${i}-lang`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="es">{t("langEs")}</SelectItem>
                    <SelectItem value="en">{t("langEn")}</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  variant="ghost"
                  size="icon"
                  className="col-span-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={() => removeRecipient(i)}
                  data-testid={`weekly-recipient-${i}-remove`}
                >
                  <Trash size={16} />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-100">
        <Button
          onClick={save}
          disabled={saving}
          data-testid="weekly-save"
          className="bg-blue-900 hover:bg-blue-800 gap-2"
        >
          {saving ? t("saving") : t("save")}
        </Button>
        <Button
          variant="outline"
          onClick={preview}
          disabled={previewing}
          data-testid="weekly-preview"
          className="gap-2"
        >
          <Eye size={16} /> {t("previewEmail")}
        </Button>
        <Button
          variant="outline"
          onClick={sendNow}
          disabled={sending || !cfg.recipients.length}
          data-testid="weekly-send-now"
          className="gap-2 text-blue-900 border-blue-200 hover:bg-blue-50"
        >
          <PaperPlaneTilt size={16} weight="bold" />
          {sending ? t("sendingNow") : t("sendNow")}
        </Button>
      </div>

      {history.length > 0 && (
        <div className="mt-8 pt-6 border-t border-slate-100">
          <Label className="overline-label mb-3 block">{t("weeklyHistory")}</Label>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="weekly-history-table">
              <thead className="text-xs text-slate-500 uppercase">
                <tr className="border-b border-slate-200">
                  <th className="text-left py-2 px-2">{t("lastSent")}</th>
                  <th className="text-left py-2 px-2">{t("weekIso")}</th>
                  <th className="text-left py-2 px-2">{t("triggeredBy")}</th>
                  <th className="text-right py-2 px-2">{t("sentOk")}</th>
                  <th className="text-right py-2 px-2">{t("sentFail")}</th>
                </tr>
              </thead>
              <tbody>
                {history.slice(0, 10).map((h) => (
                  <tr key={h.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-2 px-2 text-slate-700">{(h.sent_at || "").replace("T", " ").slice(0, 16)}</td>
                    <td className="py-2 px-2 font-mono text-xs">{h.week_iso}</td>
                    <td className="py-2 px-2 text-slate-500 text-xs">{h.triggered_by}</td>
                    <td className="py-2 px-2 text-right text-green-700 font-semibold">{h.ok_count}</td>
                    <td className="py-2 px-2 text-right text-red-600 font-semibold">{h.fail_count > 0 ? h.fail_count : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
